import {
    Subject
} from "rxjs";

import {
    TilbyTcpSocket,
    TilbyTcpSocketConnectOptions,
    TilbyTcpSocketServer,
    TilbyTcpSocketUtils
} from "./tilby-tcp-socket";

import {
    TilbyCdvTCPServer,
    TilbyCdvTCPSocket
} from "app/types";

export class CordovaTcpSocketServer implements TilbyTcpSocketServer {
    private server?: TilbyCdvTCPServer; // Reference to the Cordova TCP server object
    private port?: number;
    private connectionSubject = new Subject<TilbyTcpSocket>();

    public onConnection = this.connectionSubject.asObservable();

    private onErrorSubject = new Subject<string>();
    public onError = this.onErrorSubject.asObservable();

    constructor() {
        if (!window.tilbyTCP) {
            throw new Error('Cordova TCP plugin not available.');
        }

        this.server = window.tilbyTCP.createServer((socket) => {
            // When a new connection comes in, wrap it in our CordovaTcpSocket
            CordovaTcpSocketServer.logEvent(`New connection from ${socket.remoteAddress}:${socket.remotePort} (ID: ${socket.id})`);
            this.connectionSubject.next(new CordovaTcpSocket(socket));
        });

        this.server.onError = (error: any) => {
            CordovaTcpSocketServer.logEvent(`Server error: ${error}`);
            this.onErrorSubject.next(error);
        };

        this.server.onClose = () => {
            CordovaTcpSocketServer.logEvent(`Server closed`);
            this.connectionSubject.complete();
            this.onErrorSubject.complete();
        };
    }

    private static logEvent(...message: any[]) {
        console.debug('[ CordovaTcpSocketServer ]', ...message);
    }

    public listen(port: number): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.server) {
                return reject('SERVER_NOT_OPEN');
            }

            this.port = port;
            this.server.listen({ port: port, host: '0.0.0.0' }, () => {
                CordovaTcpSocketServer.logEvent(`Listening on port ${port}`);
                resolve();
            }, (error: any) => {
                CordovaTcpSocketServer.logEvent(`Listen error: ${error}`);
                reject(error);
            });
        });
    }

    public close(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.server) {
                return reject('SERVER_NOT_OPEN');
            }

            this.server.close(() => {
                CordovaTcpSocketServer.logEvent(`Server on port ${this.port} closed`);
                resolve();
            }, (error: any) => {
                CordovaTcpSocketServer.logEvent(`Close error: ${error}`);
                reject(error);
            });
        });
    }
}

export class CordovaTcpSocket implements TilbyTcpSocket {
    private socket?: TilbyCdvTCPSocket; // Reference to the Cordova TCP socket object
    private socketId: string; // Cordova plugin uses string IDs
    private _remoteAddress?: string;
    private _remotePort?: number;

    private onDataSubject = new Subject<Uint8Array>();
    private onErrorSubject = new Subject<string>();

    public onData = this.onDataSubject.asObservable();
    public onError = this.onErrorSubject.asObservable();

    public get remoteAddress(): string | undefined {
        return this._remoteAddress;
    }

    public get remotePort(): number | undefined {
        return this._remotePort;
    }

    private static logEvent(...message: any[]) {
        console.debug('[ CordovaTcpSocket ]', ...message);
    }

    constructor(cordovaSocket?: TilbyCdvTCPSocket) {
        if (!window.tilbyTCP) {
            throw new Error('Cordova TCP plugin not available.');
        }

        if (cordovaSocket) {
            this.socket = cordovaSocket;
            this._remoteAddress = cordovaSocket.remoteAddress;
            this._remotePort = cordovaSocket.remotePort;
            this.socketId = cordovaSocket.id;
        } else {
            this.socket = window.tilbyTCP.createSocket();
            this.socketId = this.socket.id;
        }

        this.socket.onData = (data: Uint8Array) => {
            CordovaTcpSocket.logEvent(`Received packet from socket ${this.socketId}:`, TilbyTcpSocketUtils.ab2Hex(data));
            this.onDataSubject.next(data);
        };

        this.socket.onError = (error: any) => {
            CordovaTcpSocket.logEvent(`Received error from socket ${this.socketId}:`, error);
            this.onErrorSubject.next(error);
        };

        this.socket.onClose = () => {
            CordovaTcpSocket.logEvent(`Socket ${this.socketId} closed`);
            this.onDataSubject.complete();
            this.onErrorSubject.complete();
        };
    }

    public connect(ipAddress: string, port: number, options: TilbyTcpSocketConnectOptions): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.socket) {
                return reject('SOCKET_NOT_OPEN');
            }

            const onConnectSuccess = () => {
                // For client-initiated connections, store the requested address/port
                // The actual connected address/port might differ due to NAT, but this is usually sufficient.
                this._remoteAddress = ipAddress;
                this._remotePort = port;
                CordovaTcpSocket.logEvent(`Socket ${this.socketId} connected to ${ipAddress}:${port}`);
                resolve();
            };

            const onConnectError = (error: any) => {
                CordovaTcpSocket.logEvent(`Connection error for socket ${this.socketId}:`, error);
                reject(error);
            };

            this.socket.connect({ host: ipAddress, port: port, timeout: options.timeout }, onConnectSuccess, onConnectError);
        });
    }

    public send(data: Uint8Array): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.socket) {
                return reject('SOCKET_NOT_OPEN');
            }

            this.socket.write(data, () => {
                CordovaTcpSocket.logEvent(`Sent packet to socket ${this.socketId}:`, TilbyTcpSocketUtils.ab2Hex(data));
                resolve();
            }, (error: any) => {
                CordovaTcpSocket.logEvent(`Write error for socket ${this.socketId}:`, error);
                reject(error);
            });
        });
    }

    public async disconnect(): Promise<void> {
        return this.close(); // Disconnect is equivalent to close for this implementation
    }

    public async close(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.socket) {
                return resolve();
            }

            this.socket.close(() => {
                CordovaTcpSocket.logEvent(`Socket ${this.socketId} closed`);
                this.onDataSubject.complete();
                this.onErrorSubject.complete();
                this.socket = undefined;
                resolve();
            }, (error: any) => {
                CordovaTcpSocket.logEvent(`Close error for socket ${this.socketId}:`, error);
                reject(error);
            });
        });
    }
}
